import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "485",
  "slug": "what-is-permafrost-frozen-ground-explained",
  "title": "What Is Permafrost? The Frozen Ground Beneath the Far North",
  "excerpt": "Permafrost is ground that stays frozen year after year, locking away ancient carbon under the Arctic. Here is how it forms, where it is found, and why its thaw matters far beyond the poles.",
  "date": "2026-06-19",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1675536463583-a543fe6abe77?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhcmN0aWMlMjB0dW5kcmElMjBwZXJtYWZyb3N0fGVufDF8MHx8fDE3ODE4NTY3ODd8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "3 min",
  "author": "EarthGuessr Team",
  "tldr": "Permafrost is any ground — soil, sediment, or rock — that stays at or below freezing for at least two years in a row. It underlies roughly a quarter of the Northern Hemisphere's land, stores vast amounts of carbon, and is increasingly thawing as the Arctic warms.",
  "content": [
    {
      "type": "paragraph",
      "text": "Across the far north of Russia, Canada and Alaska lies a hidden layer that shapes the whole landscape above it: ground that has not thawed for centuries, in some places not for tens of thousands of years. It is called permafrost, and although you rarely see it directly, its presence — and increasingly its absence — affects rivers, forests, buildings and even the global climate."
    },
    {
      "type": "heading",
      "text": "What Is Permafrost?"
    },
    {
      "type": "paragraph",
      "text": "Permafrost is defined by temperature, not by ice. It is any ground — soil, gravel, sand or bedrock — that remains at or below zero degrees Celsius for at least two consecutive years. Often it does contain large amounts of frozen water that cements the ground together like rock, but the definition is purely about staying frozen, year after year."
    },
    {
      "type": "paragraph",
      "text": "Above the permafrost sits a thin layer called the active layer, which thaws each summer and refreezes each winter. Plants root in this active layer, and its seasonal melting and freezing is what gives much of the Arctic its lumpy, waterlogged surface. Below the active layer, the permafrost itself can be anything from a metre to over a kilometre thick in the coldest regions."
    },
    {
      "type": "heading",
      "text": "How Permafrost Forms"
    },
    {
      "type": "paragraph",
      "text": "Permafrost forms wherever the ground loses more heat over the year than it gains, so that the deeper soil never has a chance to warm above freezing. That happens mainly in two settings: across the high latitudes near the poles, and high in mountains where altitude keeps temperatures low — so-called alpine permafrost found even in mid-latitude ranges. A blanket of snow, peat or vegetation can insulate the ground and help preserve the frozen layer beneath through the short Arctic summer."
    },
    {
      "type": "heading",
      "text": "Where Permafrost Is Found"
    },
    {
      "type": "paragraph",
      "text": "Permafrost underlies roughly a quarter of the exposed land in the Northern Hemisphere. Geographers usually divide it into zones based on how complete the coverage is:"
    },
    {
      "type": "list",
      "items": [
        "Continuous permafrost: frozen ground underlies almost the entire area, typical of the high Arctic in Siberia, northern Canada and northern Alaska.",
        "Discontinuous permafrost: frozen ground is patchy, found in some spots and absent in warmer, sunnier ones, common a little further south.",
        "Sporadic and isolated permafrost: only scattered pockets survive, often protected by peat bogs or shade near the southern limit.",
        "Alpine permafrost: high-elevation frozen ground in mountain ranges, including some far from the poles."
      ]
    },
    {
      "type": "heading",
      "text": "Why Thawing Permafrost Matters"
    },
    {
      "type": "paragraph",
      "text": "Permafrost is more than a geographic curiosity. Frozen into it are the remains of ancient plants and animals — a huge store of carbon built up over tens of thousands of years. When permafrost thaws, microbes begin to break that material down, releasing carbon dioxide and methane into the atmosphere. Because those are greenhouse gases, the thaw can drive further warming, which thaws still more permafrost: a feedback loop that scientists watch closely."
    },
    {
      "type": "paragraph",
      "text": "There are local effects too. When ice-rich permafrost melts, the ground can slump, crack and collapse into uneven hollows and ponds, a process called thermokarst. Roads buckle, buildings tilt and pipelines are put under strain, which is why engineering in the Arctic has to treat the frozen ground as a foundation that can no longer be taken for granted."
    },
    {
      "type": "heading",
      "text": "A Landscape Worth Recognising"
    },
    {
      "type": "paragraph",
      "text": "Polygonal ground and thaw lakes can suggest permafrost terrain, but an image alone does not establish that the ground remains frozen. Permafrost occurs at high latitudes and at high elevations. Check ground-temperature or permafrost maps before assigning the label."
    }
  ]
};
